import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AiAdminStatus, AiFailedJob } from "@xinghai/shared";
import { Button, EmptyState, Icon, Table, useToast } from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { adminAiApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { useAiFlag } from "../ai/useAiEnabled";
import { KeyValue, LoadingBlock, PageHeader, QueryBoundary, Section } from "./common";

function rateLabel(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 1000) / 10}%`;
}

function countLine(counts: AiAdminStatus["counts"]): string {
  return `排队 ${counts.queued} · 评分中 ${counts.running} · 完成 ${counts.done} · 失败 ${counts.failed} · 跳过 ${counts.skipped}`;
}

export function AiPage() {
  const { me, isLoading } = useAuth();
  const ai = useAiFlag();
  const isAdmin = (me?.user.roles ?? []).includes("admin");

  if (isLoading || (isAdmin && ai.pending)) {
    return (
      <div className="xz-adm">
        <PageHeader title="AI 评分" />
        <LoadingBlock rows={2} label="正在检查权限" />
      </div>
    );
  }

  if (!isAdmin || !ai.enabled) {
    return (
      <div className="xz-adm">
        <PageHeader title="AI 评分" />
        <EmptyState
          icon={<Icon id="ui-lock" size={24} />}
          title={isAdmin ? "AI 评分未开启" : "无权限"}
          description={
            isAdmin
              ? "设置 ZY_AI_ENABLED 之后，提交的录音会自动评分。结果仅供参考。"
              : "AI 评分队列只对系统管理员开放。"
          }
        />
      </div>
    );
  }

  return <AiAdmin />;
}

function AiAdmin() {
  const toast = useToast();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: qk.adminAi,
    queryFn: ({ signal }) => adminAiApi.status(signal),
    refetchInterval: (query) => {
      const counts = query.state.data?.ai.counts;
      if (!counts) return false;
      return counts.queued > 0 || counts.running > 0 ? 4000 : false;
    },
  });

  const retry = useMutation({
    mutationFn: (recordingId: string) => adminAiApi.retry(recordingId),
    onSuccess: () => {
      toast.success("已重新排队");
      void qc.invalidateQueries({ queryKey: qk.adminAi });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="xz-adm">
      <PageHeader title="AI 评分" description="每条录音只评一次。本版AI结果仅供参考。" />
      <QueryBoundary
        isPending={status.isPending}
        isError={status.isError}
        error={status.error}
        onRetry={() => void status.refetch()}
      >
        {status.data && (
          <AiBody
            ai={status.data.ai}
            busyId={retry.isPending ? retry.variables : null}
            onRetry={(id) => retry.mutate(id)}
          />
        )}
      </QueryBoundary>
    </div>
  );
}

function AiBody({
  ai,
  busyId,
  onRetry,
}: {
  ai: AiAdminStatus;
  busyId: string | null | undefined;
  onRetry: (id: string) => void;
}) {
  const columns: Column<AiFailedJob>[] = [
    { key: "id", header: "录音", render: (row) => row.recording_id },
    { key: "error_type", header: "类型", render: (row) => row.error_type ?? "—" },
    { key: "error", header: "原因", render: (row) => row.error ?? "评分失败" },
    { key: "attempts", header: "次数", render: (row) => String(row.attempts), align: "end" },
    {
      key: "retry",
      header: "",
      render: (row) => (
        <Button
          size="sm"
          disabled={busyId === row.recording_id}
          onClick={() => onRetry(row.recording_id)}
        >
          重试
        </Button>
      ),
    },
  ];

  const ready = ai.gateway_ready === null ? "未配置" : ai.gateway_ready ? "就绪" : "未就绪";

  return (
    <div className="xz-stack">
      <Section title="今日用量">
        <KeyValue
          items={[
            { key: "day", label: "日期（上海）", value: ai.daily.day },
            { key: "used", label: "已占用", value: `${ai.daily.used} / ${ai.daily.limit}` },
            { key: "queue", label: "队列", value: countLine(ai.counts) },
          ]}
        />
      </Section>
      <Section title="缓存命中">
        <KeyValue
          items={[
            { key: "all", label: "全部", value: rateLabel(ai.cache.hit_rate) },
            { key: "today", label: "今日", value: rateLabel(ai.cache_today.hit_rate) },
          ]}
        />
        <p className="gz-caption">
          缓存读 / (缓存读 + 未缓存输入)。全部 {ai.cache.cache_read_tokens} / (
          {ai.cache.cache_read_tokens} + {ai.cache.input_tokens})。
        </p>
      </Section>
      <Section title="服务">
        <KeyValue
          items={[
            { key: "provider", label: "音频通道", value: ai.provider },
            { key: "model", label: "模型", value: ai.model },
            {
              key: "gateway",
              label: "评分服务",
              value: ai.gateway_configured ? "已配置" : "未配置",
            },
            { key: "ready", label: "就绪", value: ready },
            { key: "mimo", label: "MiMo 直连", value: ai.mimo_configured ? "已配置" : "未配置" },
            { key: "asr", label: "识别提示", value: ai.asr ? "开" : "关" },
            { key: "backfill", label: "回填", value: ai.backfill ? "开" : "关" },
            { key: "concurrency", label: "并发", value: String(ai.concurrency) },
          ]}
        />
      </Section>
      <Section title="失败">
        <Table
          caption="失败的评分"
          columns={columns}
          rows={ai.failed}
          rowKey={(row) => row.recording_id}
          empty={<p className="gz-muted">没有失败的评分。</p>}
        />
      </Section>
    </div>
  );
}
